import type { LucideIcon } from 'lucide-react'
import { AlertTriangle, CheckCircle2, Circle } from 'lucide-react'
import { Button } from '../../components/ui/Button'

export type ServiceKind = 'ga4' | 'gsc' | 'ads' | 'gbp'
export type ServiceStatus = 'connected' | 'not_connected' | 'scope_missing' | 'reauth_required'

export interface ServiceConnectionCardProps {
  service: ServiceKind
  status: ServiceStatus
  accountEmail?: string | null
  onConnect: () => void
  onAuthorize: () => void
  onReconnect: () => void
  onChangeAccount: () => void
  onDisconnect?: () => void
}

const SERVICE_LABELS: Record<ServiceKind, string> = {
  ga4: 'Google Analytics 4',
  gsc: 'Search Console',
  ads: 'Google Ads',
  gbp: "Fiche d'établissement (GBP)",
}

interface StatusConfig {
  badgeLabel: string
  badgeIcon: LucideIcon
  badgeClass: string
  ctaLabel: (serviceLabel: string) => string
  action: keyof Pick<
    ServiceConnectionCardProps,
    'onConnect' | 'onAuthorize' | 'onReconnect' | 'onChangeAccount'
  >
  ctaVariant: 'primary' | 'secondary'
}

const STATUS_CONFIG: Record<ServiceStatus, StatusConfig> = {
  connected: {
    badgeLabel: 'Connecté',
    badgeIcon: CheckCircle2,
    badgeClass: 'text-emerald-600 dark:text-emerald-400',
    ctaLabel: () => 'Changer de compte',
    action: 'onChangeAccount',
    ctaVariant: 'secondary',
  },
  not_connected: {
    badgeLabel: 'Non connecté',
    badgeIcon: Circle,
    badgeClass: 'text-[#8B8FA7]',
    ctaLabel: () => 'Connecter un compte',
    action: 'onConnect',
    ctaVariant: 'primary',
  },
  scope_missing: {
    badgeLabel: 'Autorisation incomplète',
    badgeIcon: AlertTriangle,
    badgeClass: 'text-amber-600 dark:text-amber-400',
    ctaLabel: (serviceLabel) => `Autoriser ${serviceLabel}`,
    action: 'onAuthorize',
    ctaVariant: 'primary',
  },
  reauth_required: {
    badgeLabel: 'Reconnexion requise',
    badgeIcon: AlertTriangle,
    badgeClass: 'text-red-600 dark:text-red-400',
    ctaLabel: () => 'Reconnecter',
    action: 'onReconnect',
    ctaVariant: 'primary',
  },
}

export default function ServiceConnectionCard({
  service,
  status,
  accountEmail,
  onConnect,
  onAuthorize,
  onReconnect,
  onChangeAccount,
  onDisconnect,
}: ServiceConnectionCardProps) {
  const serviceLabel = SERVICE_LABELS[service]
  const config = STATUS_CONFIG[status]
  const BadgeIcon = config.badgeIcon

  const actions = { onConnect, onAuthorize, onReconnect, onChangeAccount }
  const handleClick = actions[config.action]

  return (
    <div className="rounded-2xl border border-light-border dark:border-dark-border bg-light-surface dark:bg-dark-surface p-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-sm font-semibold text-[#1B2559] dark:text-white">{serviceLabel}</p>
          <div className={['mt-1.5 flex items-center gap-1.5 text-xs font-medium', config.badgeClass].join(' ')}>
            <BadgeIcon className="w-3.5 h-3.5" strokeWidth={2} />
            <span>{config.badgeLabel}</span>
          </div>
          {status === 'connected' && accountEmail && (
            <p className="mt-1 text-xs text-[#8B8FA7]">{accountEmail}</p>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {status === 'connected' && onDisconnect && (
            <Button variant="secondary" onClick={onDisconnect}>
              Déconnecter
            </Button>
          )}
          <Button variant={config.ctaVariant} onClick={handleClick}>
            {config.ctaLabel(serviceLabel)}
          </Button>
        </div>
      </div>
    </div>
  )
}
